import { LoaderCircle, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { CHAT_WIDGET_SCRIPT_INTEGRITY, URLS } from "@/lib/config";

declare global {
  interface Window {
    /** Read by the widget bundle's createCdnMount installer. */
    FuristicChatConfig?: {
      backendUrl?: string;
      socketUrl?: string;
      theme?: "light" | "dark";
      sessionId?: string;
      product?: "platform";
      _isInternal?: boolean;
    };
    /**
     * Handle installed by the widget bundle's host-kit CDN mount
     * (`window.FuristicChat = handle`) once the widget is mounted.
     */
    FuristicChat?: { open(): void };
  }
}

const WIDGET_SCRIPT_ID = "platform-chat-widget-script";

function configureWidget() {
  window.FuristicChatConfig = {
    backendUrl: URLS.CHAT_BACKEND,
    socketUrl: URLS.SOCKET_IO,
    product: "platform",
  };
}

function loadWidgetScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(
      WIDGET_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing?.dataset.loaded === "true") {
      resolve();
      return;
    }

    const script = existing ?? document.createElement("script");
    script.id = WIDGET_SCRIPT_ID;
    script.src = URLS.WIDGET_SCRIPT;
    script.async = true;
    // Content-hash pin (task 8.5): the browser refuses any bundle whose bytes
    // do not match the build-time-resolved digest.
    if (CHAT_WIDGET_SCRIPT_INTEGRITY) {
      script.integrity = CHAT_WIDGET_SCRIPT_INTEGRITY;
      script.crossOrigin = "anonymous";
    }
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true },
    );
    script.addEventListener("error", () => reject(new Error("load failed")), {
      once: true,
    });
    if (!existing) document.body.appendChild(script);
  });
}

/**
 * Wait for the kit's CDN mount to install its handle (it bootstraps the
 * origin-bound embed token before mounting), then open the assistant. The
 * handle IS the readiness signal.
 */
async function openViaKitHandle(timeoutMs = 15_000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const handle = window.FuristicChat;
    if (handle) {
      handle.open();
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  throw new Error("initialization timed out");
}

/**
 * Hydration signal without a setState-in-effect: false during SSR, true in the
 * browser. The launcher stays disabled until hydration so a server-rendered
 * click can never fire.
 */
function subscribeHydration() {
  return () => {};
}

export function LandingChatbotWidget() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const isHydrated = useSyncExternalStore(
    subscribeHydration,
    () => true,
    () => false,
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    configureWidget();
  }, []);

  const openAssistant = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    setLoadError(null);
    configureWidget();
    try {
      await loadWidgetScript();
      await openViaKitHandle();
      setIsLoaded(true);
    } catch {
      document.getElementById(WIDGET_SCRIPT_ID)?.remove();
      setIsLoading(false);
      setLoadError("The Furistic assistant is temporarily unavailable.");
    }
  }, [isLoading]);

  if (isLoaded) return null;

  return (
    <>
      {loadError ? (
        <p
          id="assistant-load-error"
          role="status"
          className="fixed bottom-24 right-5 z-50 max-w-64 rounded-md bg-slate-950 px-3 py-2 text-sm text-white shadow-lg sm:right-6"
        >
          {loadError}
        </p>
      ) : null}
      <button
        type="button"
        aria-label="Open Furistic assistant"
        aria-describedby={loadError ? "assistant-load-error" : undefined}
        title="Open Furistic assistant"
        data-hydrated={isHydrated}
        disabled={!isHydrated || isLoading}
        onClick={() => void openAssistant()}
        className="fixed bottom-5 right-5 z-50 grid size-14 place-items-center rounded-full bg-violet-600 text-white shadow-lg transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:bg-violet-500 sm:bottom-6 sm:right-6"
      >
        {isLoading ? (
          <LoaderCircle
            aria-hidden="true"
            className="size-6 motion-safe:animate-spin"
          />
        ) : (
          <MessageCircle aria-hidden="true" className="size-6" />
        )}
      </button>
    </>
  );
}
